import { CHARACTERS, type CharacterSpec, type Entity, type WeaponId } from '@cuboom/shared';
import { drawHeldWeaponArt } from './projectileArt.js';

/**
 * Procedural species art — layered shapes in local space (feet anchor at the
 * entity position), mirrored by facing. Squash/stretch from velocity, eyes
 * that track the aim point, pose feedback (idle bob, walk sway, air stretch,
 * hurt flash, celebrate hop, dead fall). No skeletal system: pose is a few
 * scalar channels evaluated per frame.
 */

/**
 * Play-view scale: the field draws soldiers at twice the authored size so
 * they read on a phone, where the camera sits at its 0.3 zoom floor. Menus,
 * the icon generator and the art showcase stay at 1× (no `scale` passed).
 */
export const CHAR_SCALE = 2;

export interface CharDrawOpts {
  /** The turn marker (floating arrow) is drawn by the caller; art only needs these: */
  active: boolean;
  /** World point eyes should look at (mouse / target); null = face forward. */
  aim?: { x: number; y: number } | null;
  /** Wall-clock seconds for idle/breath cycles. */
  t: number;
  /**
   * Weapon currently in this character's hands (drawn as held gear). Only the
   * active character gets one; the launchers draw their bespoke tube and every
   * other weapon rides the grip as its own flight art, swinging with `aim`.
   */
  held?: WeaponId | null;
  /**
   * Transient face animation: 'jump' = wide excited eyes + open mouth on
   * takeoff, 'land' = happy closed-arc eyes + big smile on touchdown.
   */
  mood?: 'jump' | 'land' | null;
  /**
   * Launch kick, 1 → 0 over ~0.24 s from the jump_intent event: a tall
   * rubber-band stretch the instant the feet leave the ground.
   */
  takeoff?: number;
  /**
   * Touchdown squash, 1 → 0 over ~0.3 s from the land event: flatten wide
   * against the ground, then spring back. Only while actually grounded.
   */
  squash?: number;
  /**
   * Uniform world scale over the authored art — the play view passes
   * CHAR_SCALE; omitted = 1× (menus, icon, showcase).
   */
  scale?: number;
}

function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}

/**
 * Per-frame face mood, set by drawCharacter and read by the eye/mouth
 * helpers. Drawing is synchronous, so one module slot is enough.
 */
let faceMood: 'jump' | 'land' | null = null;

/** Eye pair with pupils biased toward `lookX` (-1..1), plus shine + blush. */
function drawEyes(
  ctx: CanvasRenderingContext2D,
  spec: CharacterSpec,
  dx: number,
  dy: number,
  gap: number,
  r: number,
  lookX: number,
  lookY: number,
  goggle: boolean,
): void {
  if (faceMood === 'land') {
    // Touchdown squint: happy closed arcs (^ ^), blush kept for the aww.
    ctx.lineCap = 'round';
    for (const side of [-1, 1]) {
      const ex = dx + side * gap;
      if (goggle) {
        ctx.strokeStyle = 'rgba(30,30,40,0.85)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ex, dy, r + 2.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.strokeStyle = spec.palette.eye;
      ctx.lineWidth = Math.max(1.6, r * 0.42);
      ctx.beginPath();
      ctx.arc(ex, dy + r * 0.45, r * 0.95, Math.PI * 1.12, Math.PI * 1.88);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,120,135,0.32)';
      ctx.beginPath();
      ctx.ellipse(ex + side * (r + 3.2), dy + r + 1.4, r * 0.8, r * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
  if (faceMood === 'jump') {
    r *= 1.3; // wide excited eyes
    lookY = Math.min(lookY, -0.4); // gaze up where they're soaring
  }
  for (const side of [-1, 1]) {
    const ex = dx + side * gap;
    if (goggle) {
      ctx.strokeStyle = 'rgba(30,30,40,0.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ex, dy, r + 2.5, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(ex, dy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = spec.palette.eye;
    ctx.beginPath();
    ctx.arc(ex + lookX * r * 0.4, dy + lookY * r * 0.4, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
    // Anime-style catchlight — cheap, and it doubles the cute instantly.
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.beginPath();
    ctx.arc(ex + lookX * r * 0.4 - r * 0.22, dy + lookY * r * 0.4 - r * 0.28, r * 0.24, 0, Math.PI * 2);
    ctx.fill();
    // Blush just outside/below each eye. A colored muzzle owns the snout
    // side (blush on the pink turns muddy), and the elephant's raised trunk
    // and pale inner ear own the cheeks — no blush on trunk faces at all.
    const frontSide = dx >= 0 ? 1 : -1;
    const blushOk = !spec.quirks.includes('trunk')
      && !(spec.quirks.includes('muzzle') && side === frontSide);
    if (blushOk) {
      ctx.fillStyle = 'rgba(255,120,135,0.32)';
      ctx.beginPath();
      ctx.ellipse(ex + side * (r + 3.2), dy + r + 1.4, r * 0.8, r * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/** Simple mouth: grumpy = flat, chipper = smile arc, default = tiny o. */
function drawMouth(ctx: CanvasRenderingContext2D, spec: CharacterSpec, dx: number, dy: number): void {
  if (faceMood === 'jump') {
    // Open "whee!" — a small excited oval.
    ctx.fillStyle = spec.palette.eye;
    ctx.beginPath();
    ctx.ellipse(dx, dy + 3, 3.1, 3.9, 0, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (faceMood === 'land') {
    // Satisfied touchdown smile.
    ctx.strokeStyle = spec.palette.eye;
    ctx.lineWidth = 1.8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(dx, dy - 1, 5.4, 0.3, Math.PI - 0.3);
    ctx.stroke();
    return;
  }
  ctx.strokeStyle = spec.palette.eye;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  if (spec.quirks.includes('grumpy')) {
    ctx.arc(dx, dy + 5, 5, Math.PI * 1.15, Math.PI * 1.85);
  } else if (spec.quirks.includes('chipper')) {
    ctx.arc(dx, dy + 1, 5, 0.25, Math.PI - 0.25);
  } else {
    ctx.arc(dx, dy + 2, 2.4, 0, Math.PI * 2);
  }
  ctx.stroke();
}

export function drawCharacter(
  ctx: CanvasRenderingContext2D,
  e: Entity,
  spec: CharacterSpec,
  o: CharDrawOpts,
): void {
  const t = o.t;
  const dead = !e.alive || e.pose === 'dead';
  const hurt = e.pose === 'hurt' && e.poseTimer > 0;
  // Face mood lasts only for this draw (dead/hurt faces keep their own look).
  faceMood = !dead && !hurt ? (o.mood ?? null) : null;

  // --- global channels ------------------------------------------------------
  ctx.save();
  ctx.translate(e.x, e.y);
  const scale = o.scale ?? 1;
  if (scale !== 1) ctx.scale(scale, scale); // uniform: dead-fall/squash below stay proportional
  if (dead) {
    // Topple sideways like a felled sprout.
    ctx.globalAlpha *= 0.75;
    ctx.rotate(e.facing * Math.PI * 0.42);
    ctx.translate(0, 4);
  }

  // Squash & stretch: airborne velocity stretches, ground contact squashes,
  // idle breathes, celebrate hops. Jump takeoff/landing add one-shot pops on
  // top so launching reads as a spring release and touchdown reads as impact.
  let sx = 1;
  let sy = 1;
  if (!dead) {
    if (!e.onGround) {
      sy = clamp(1 + Math.abs(e.vy) * 0.00045, 1, 1.3);
      sx = 1 / Math.sqrt(sy);
      const k = clamp(o.takeoff ?? 0, 0, 1);
      if (k > 0) {
        sy *= 1 + 0.28 * k; // rocket upward the moment it leaves
        sx *= 1 - 0.16 * k;
      }
    } else {
      const breath = 1 + Math.sin(t * 2.2 + e.id) * 0.025;
      sy = breath;
      sx = 2 - breath;
      const k = clamp(o.squash ?? 0, 0, 1);
      if (k > 0) {
        sy *= 1 - 0.34 * k; // pancake on impact, spring back as k → 0
        sx *= 1 + 0.3 * k;
      }
    }
    if (e.pose === 'celebrate') {
      const hop = Math.abs(Math.sin(t * 9));
      ctx.translate(0, -hop * 7);
      sy *= 1 + hop * 0.1;
      sx *= 1 - hop * 0.06;
    } else if (e.pose === 'walk') {
      ctx.rotate(Math.sin(t * 11 + e.id) * 0.07);
    }
    if (hurt) {
      ctx.translate(Math.sin(t * 55) * 2.2, 0); // rattle
    }
  }
  ctx.scale(sx, sy);

  // Eyes look at the aim point (or along facing). Local space is NOT
  // mirrored by facing, so the normalized offset is already world-correct.
  const lookXRaw = o.aim ? o.aim.x - e.x : e.facing * 30;
  const lookYRaw = o.aim ? o.aim.y - (e.y - 18) : -6;
  const lookLen = Math.hypot(lookXRaw, lookYRaw) || 1;
  const lookX = clamp(lookXRaw / lookLen, -1, 1);
  const lookY = clamp(lookYRaw / lookLen, -1, 1);

  const P = spec.palette;
  const goggle = spec.quirks.includes('goggle-eyes');

  // --- body by plan ---------------------------------------------------------
  switch (spec.body) {
    case 'stalk': {
      // Rounded column (corn, thistle): kept short so no one reads as a tree.
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.moveTo(-7, 0);
      ctx.quadraticCurveTo(-9, -18, -7, -28);
      ctx.quadraticCurveTo(0, -34, 7, -28);
      ctx.quadraticCurveTo(9, -18, 7, 0);
      ctx.closePath();
      ctx.fill();
      // Shade edge.
      ctx.fillStyle = P.shade;
      ctx.beginPath();
      ctx.moveTo(3, 0);
      ctx.quadraticCurveTo(6, -16, 4, -29);
      ctx.quadraticCurveTo(7, -31, 7, -27);
      ctx.quadraticCurveTo(9, -16, 7, 0);
      ctx.closePath();
      ctx.fill();
      // Head disk (flower face / crown).
      ctx.fillStyle = spec.quirks.includes('petals') ? P.accent : P.body;
      if (spec.quirks.includes('petals')) {
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2 + Math.sin(t * 1.4) * 0.05;
          ctx.beginPath();
          ctx.ellipse(Math.cos(a) * 8.5, -34 + Math.sin(a) * 8.5, 6, 3.6, a, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = P.body;
      }
      ctx.beginPath();
      ctx.arc(0, -34, 9, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('spines')) {
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const y = -4 - i * 3;
          const w = 7 + Math.sin(i * 2.1) * 1.5;
          ctx.moveTo(-w, y);
          ctx.lineTo(-w - 3, y - 2);
          ctx.moveTo(w, y);
          ctx.lineTo(w + 3, y - 2);
        }
        ctx.stroke();
      }
      if (spec.quirks.includes('kernels')) {
        // Corn: kernel grid on the face disk.
        ctx.fillStyle = 'rgba(120, 86, 16, 0.55)';
        for (let gy = -2; gy <= 2; gy++) {
          for (const gx of [-5, 0, 5]) {
            ctx.beginPath();
            ctx.arc(gx + (gy % 2 ? 2.5 : 0), -34 + gy * 3.2, 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      drawEyes(ctx, spec, 0, -36, 3.6, 3.6, lookX, lookY, goggle);
      drawMouth(ctx, spec, 0, -34);
      break;
    }
    case 'blob': {
      // Wide soft mound (mushroom-ish): dome + flared skirt.
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.ellipse(0, -12, 15, 13, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.shade;
      ctx.beginPath();
      ctx.ellipse(4, -9, 9, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      // Cap / collar accent.
      ctx.fillStyle = P.accent;
      ctx.beginPath();
      ctx.ellipse(0, -20, 13, 6, 0, Math.PI, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('shell')) {
        // Snail: a swirled shell saddle on the back.
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.arc(-7, -18, 8.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(40,26,10,0.65)';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        for (let a = 0; a < Math.PI * 4; a += 0.25) {
          const rr = 1.2 + a * 1.05;
          const px = -7 + Math.cos(a) * rr;
          const py = -18 + Math.sin(a) * rr;
          if (a === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
      drawEyes(ctx, spec, 0, -13, 6, 3.9, lookX, lookY, goggle);
      drawMouth(ctx, spec, 0, -8);
      break;
    }
    case 'round': {
      // Perfect-ish sphere (frog, squirrel, acorn, owl body).
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.arc(0, -14, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.shade;
      ctx.beginPath();
      ctx.arc(5, -10, 8, 0, Math.PI * 2);
      ctx.fill();
      // Belly patch.
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.beginPath();
      ctx.ellipse(0, -9, 7.5, 6.5, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('ears')) {
        // Bunny ears: tall, soft, with a pink inner line that wiggles.
        const wig = Math.sin(t * 3 + e.id) * 0.12;
        for (const side of [-1, 1]) {
          ctx.save();
          ctx.translate(side * 5, -25);
          ctx.rotate(side * (0.22 + wig * side));
          ctx.fillStyle = P.body;
          ctx.beginPath();
          ctx.ellipse(0, -9, 4, 10.5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = P.accent;
          ctx.beginPath();
          ctx.ellipse(0, -8.5, 1.9, 7, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
      if (spec.quirks.includes('spots')) {
        ctx.fillStyle = P.accent;
        for (const [sx, sy, sr] of [[-7, -20, 3.2], [7, -21, 2.8], [-9, -11, 2.6], [9, -12, 3]]) {
          ctx.beginPath();
          ctx.arc(sx, sy, sr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      if (spec.quirks.includes('spines')) {
        // Hedgehog: a crown of quills along the back.
        ctx.fillStyle = P.accent;
        for (let i = 0; i < 7; i++) {
          const a = Math.PI * (1.05 + (i / 6) * 0.9);
          const bx = Math.cos(a) * 13;
          const by = -14 + Math.sin(a) * 13;
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.lineTo(bx * 1.28, (by + 14) * 1.28 - 14);
          ctx.lineTo(bx * 0.82 + 2, by * 0.82 - 3);
          ctx.closePath();
          ctx.fill();
        }
      }
      if (spec.quirks.includes('stem')) {
        // Pumpkin: little curly stem on top.
        ctx.strokeStyle = P.accent;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(0, -27);
        ctx.quadraticCurveTo(2, -32, -2, -33);
        ctx.stroke();
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.ellipse(3, -30, 4, 2.2, -0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      drawEyes(ctx, spec, 0, -18, 5.5, goggle ? 4.8 : 4.1, lookX, lookY, goggle);
      if (spec.quirks.includes('bill')) {
        // Duck bill replaces the mouth — flat, orange, endlessly optimistic.
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.ellipse(lookX * 1.5, -10.5, 5.6, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(140,90,10,.55)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-4 + lookX * 1.5, -10.5);
        ctx.lineTo(4 + lookX * 1.5, -10.5);
        ctx.stroke();
      } else {
        drawMouth(ctx, spec, 0, -11);
      }
      break;
    }
    case 'winged': {
      // Owl-ish: oval body with wing pads that flap when airborne.
      const flap = e.onGround ? 0 : Math.sin(t * 18) * 0.7;
      ctx.fillStyle = P.shade;
      for (const side of [-1, 1]) {
        ctx.save();
        ctx.translate(side * 11, -16);
        ctx.rotate(side * (0.5 + flap));
        ctx.beginPath();
        ctx.ellipse(0, 4, 5.5, 11, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.ellipse(0, -15, 11.5, 14, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('stripes')) {
        // Bee: bold bands across the abdomen.
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.ellipse(0, -8, 11, 2.6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(0, -13.5, 11.4, 2.6, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // Facial disc.
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.ellipse(0, -20, 8.5, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('stripes')) {
        // Antennae instead of ear tufts.
        ctx.strokeStyle = P.accent;
        ctx.lineWidth = 1.8;
        for (const side of [-1, 1]) {
          ctx.beginPath();
          ctx.moveTo(side * 4, -26);
          ctx.quadraticCurveTo(side * 7, -32, side * 10, -31);
          ctx.stroke();
          ctx.fillStyle = P.accent;
          ctx.beginPath();
          ctx.arc(side * 10, -31, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        // Ear tufts.
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.moveTo(-8, -26);
        ctx.lineTo(-10, -33);
        ctx.lineTo(-4, -28);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(8, -26);
        ctx.lineTo(10, -33);
        ctx.lineTo(4, -28);
        ctx.closePath();
        ctx.fill();
      }
      drawEyes(ctx, spec, 0, -21, 4.4, 4, lookX, lookY, !spec.quirks.includes('stripes'));
      drawMouth(ctx, spec, 0, -17);
      break;
    }
    case 'beast': {
      // Quadruped profile (dino, buffalo, turtle): body, four legs, head at
      // the facing side, tail behind. One eye pair reads as a cute ¾ view.
      const f = e.facing;
      // Tail.
      ctx.strokeStyle = P.body;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-f * 13, -13);
      ctx.quadraticCurveTo(-f * 23, -17 + Math.sin(t * 3 + e.id) * 2.5, -f * 27, -8);
      ctx.stroke();
      // Legs (stubby, with a tiny idle shift).
      ctx.fillStyle = P.shade;
      for (const lx of [-10, -3.5, 4, 10.5]) {
        ctx.beginPath();
        ctx.roundRect(lx - 2.6, -7, 5.2, 8, 2);
        ctx.fill();
      }
      // Body. The cotton-ball sheep and the round-backed elephant both puff
      // up past the default quadruped oval — everyone else stays stock.
      const puffBody = spec.quirks.includes('wool');
      const domeBody = spec.quirks.includes('trunk');
      const bodyRx = puffBody ? 14 : domeBody ? 16 : 15;
      const bodyRy = puffBody || domeBody ? 12 : 10.5;
      const bodyCy = puffBody ? -14.5 : domeBody ? -15 : -14;
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.ellipse(0, bodyCy, bodyRx, bodyRy, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.shade;
      ctx.beginPath();
      ctx.ellipse(-f * 4, bodyCy + 4, 9, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('spots')) {
        // Cow: soft ink-blot patches across the flank.
        ctx.fillStyle = P.accent;
        const patches: [number, number, number, number, number][] = [
          [-5, -18, 4.6, 3.4, 0.3],
          [6.5, -12, 3.6, 2.8, -0.4],
          [-9, -9.5, 3, 2.4, 0.2],
        ];
        for (const [px, py, prx, pry, rot] of patches) {
          ctx.beginPath();
          ctx.ellipse(px, py, prx, pry, rot, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      if (spec.quirks.includes('ridges')) {
        // Croc: a saw of scutes down the spine.
        ctx.fillStyle = P.shade;
        for (let i = 0; i < 6; i++) {
          const a = Math.PI * (1.1 + (i / 5) * 0.8);
          const bx = Math.cos(a) * 14;
          const by = -14 + Math.sin(a) * 10;
          ctx.beginPath();
          ctx.moveTo(bx - 2.6, by + 1);
          ctx.lineTo(bx * 1.22, (by + 14) * 1.24 - 14);
          ctx.lineTo(bx + 2.6, by + 1);
          ctx.closePath();
          ctx.fill();
        }
      }
      if (spec.quirks.includes('wool')) {
        // Sheep: fleece bumps all the way round — a cotton ball with a
        // face. The belly arc stays bare so the little feet show.
        ctx.fillStyle = P.body;
        const n = 11;
        const gap = 1; // radians left bare at the bottom (feet live there)
        const a0 = Math.PI / 2 + gap;
        const span = Math.PI * 2 - gap * 2;
        for (let i = 0; i < n; i++) {
          const a = a0 + (span * i) / (n - 1);
          ctx.beginPath();
          ctx.arc(Math.cos(a) * bodyRx, bodyCy + Math.sin(a) * bodyRy, 5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      if (spec.quirks.includes('shell')) {
        // Turtle: domed shell with a scute pattern.
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.ellipse(0, -19, 14, 9.5, 0, Math.PI, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(40,26,10,0.5)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(-11, -16);
        ctx.quadraticCurveTo(0, -27, 11, -16);
        ctx.moveTo(0, -27);
        ctx.lineTo(0, -13);
        ctx.stroke();
      }
      if (spec.quirks.includes('plates')) {
        // Dino: back plates.
        ctx.fillStyle = P.accent;
        for (const [px, pr] of [[-8, 4], [0, 5.5], [8, 4]]) {
          ctx.beginPath();
          ctx.moveTo(px - pr, -22);
          ctx.lineTo(px, -22 - pr * 1.4);
          ctx.lineTo(px + pr, -22);
          ctx.closePath();
          ctx.fill();
        }
      }
      // Head.
      const hx = f * 14;
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.ellipse(hx, -23, 8.5, 7.5, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('big-ears')) {
        // Elephant: a huge rounded ear laid OVER the side of the head —
        // the clip-art silhouette's signature — with a pale inner ear.
        // Eyes draw after this, so they sit in front of it.
        ctx.fillStyle = P.shade;
        ctx.beginPath();
        ctx.ellipse(hx - f * 4.5, -23.5, 8.5, 9.8, -f * 0.12, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = P.accent;
        ctx.beginPath();
        ctx.ellipse(hx - f * 4.5, -23.5, 5.6, 6.6, -f * 0.12, 0, Math.PI * 2);
        ctx.fill();
      }
      if (spec.quirks.includes('wool')) {
        // Fleece topknot over the brow.
        ctx.fillStyle = P.body;
        for (const [ox, oy, r] of [
          [-4, -29, 3.2],
          [0, -31, 3.6],
          [4, -29.5, 3],
        ]) {
          ctx.beginPath();
          ctx.arc(hx + ox, oy, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.fillStyle = P.shade; // snout
      ctx.beginPath();
      ctx.ellipse(hx + f * 6, -20, 5, 3.8, 0, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('long-snout')) {
        // Croc: long low jaw with a toothy grin.
        ctx.fillStyle = P.body;
        ctx.beginPath();
        ctx.ellipse(hx + f * 9, -19.6, 7.5, 3.3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = P.shade;
        ctx.beginPath();
        ctx.ellipse(hx + f * 12.5, -21.4, 2.6, 1.7, 0, 0, Math.PI * 2); // nostril hump
        ctx.fill();
        ctx.fillStyle = '#1e2c1a';
        ctx.beginPath();
        ctx.arc(hx + f * 13.5, -21.6, 0.9, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(20,40,18,0.85)'; // grin line
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(hx + f * 3, -18.6);
        ctx.quadraticCurveTo(hx + f * 9, -17.9, hx + f * 15.4, -18.9);
        ctx.stroke();
        ctx.fillStyle = '#fff';
        for (let i = 0; i < 4; i++) {
          const tx = hx + f * (5.5 + i * 2.6);
          ctx.beginPath();
          ctx.moveTo(tx - 1.1, -18.5);
          ctx.lineTo(tx, -16.6);
          ctx.lineTo(tx + 1.1, -18.5);
          ctx.closePath();
          ctx.fill();
        }
      }
      if (spec.quirks.includes('muzzle')) {
        // Cow: pink muzzle patch with nostrils and a smile of its own —
        // the default mouth would just be a ring lost in the pink.
        ctx.fillStyle = '#e8a0a8';
        ctx.beginPath();
        ctx.ellipse(hx + f * 6.8, -19.2, 4.7, 3.2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(140,70,80,0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#8f4b57';
        for (const dx of [5, 8.4]) {
          ctx.beginPath();
          ctx.ellipse(hx + f * dx, -20.5, 1, 1.35, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.strokeStyle = '#8f4b57';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(hx + f * 4.6, -18.1);
        ctx.quadraticCurveTo(hx + f * 6.8, -16.9, hx + f * 9, -18.1);
        ctx.stroke();
      }
      if (spec.quirks.includes('horns')) {
        ctx.fillStyle = P.accent;
        for (const [ox, oy, rot] of [[hx + f * 3, -29, 0.5], [hx - f * 3, -29, -0.5]]) {
          ctx.beginPath();
          ctx.moveTo(ox - 2.5, oy + 2);
          ctx.quadraticCurveTo(ox + f * 3, oy - 4, ox + f * 1.5, oy - 7);
          ctx.quadraticCurveTo(ox, oy - 2, ox + 2.5, oy + 2);
          ctx.closePath();
          ctx.fill();
          void rot;
        }
      }
      drawEyes(ctx, spec, hx + f * 1, -25, 3.6, 3.4, lookX, lookY, false);
      // Croc, cow and elephant all draw their own face furniture (grin /
      // muzzle smile / the trunk root itself — a mouth would sit under it).
      if (!spec.quirks.includes('long-snout') && !spec.quirks.includes('muzzle') && !spec.quirks.includes('trunk')) {
        drawMouth(ctx, spec, hx + f * 4, -19);
      }
      if (spec.quirks.includes('trunk')) {
        // Elephant: trunk raised triumphantly — thick root off the face,
        // arcing up in front of the head, tip hooked back with a sway.
        // Drawn last so it reads in front of the legs and chest.
        const sway = Math.sin(t * 2 + e.id) * 1.3;
        ctx.strokeStyle = P.body;
        ctx.lineCap = 'round';
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(hx + f * 7, -20.5);
        ctx.quadraticCurveTo(hx + f * 13.5, -25, hx + f * 13, -32);
        ctx.stroke();
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(hx + f * 13, -32);
        ctx.quadraticCurveTo(hx + f * 13.8 + sway * 0.5, -37, hx + f * 10.5 + sway, -37.5);
        ctx.stroke();
        // Wrinkle rings across the rise.
        ctx.strokeStyle = 'rgba(90,95,110,0.55)';
        ctx.lineWidth = 1;
        for (const [wx, wy] of [[hx + f * 10.5, -23.2], [hx + f * 12.4, -27.5]]) {
          ctx.beginPath();
          ctx.moveTo(wx - f * 2.8, wy + 0.6);
          ctx.lineTo(wx + f * 2.8, wy - 0.6);
          ctx.stroke();
        }
      }
      break;
    }
    case 'machine': {
      // Tractor: big rear wheel, little front wheel, boxy hood, exhaust.
      const f = e.facing;
      // Wheels.
      ctx.fillStyle = P.accent;
      ctx.beginPath();
      ctx.arc(-f * 8, -9, 9.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#8a8f96';
      ctx.beginPath();
      ctx.arc(-f * 8, -9, 3.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.accent;
      ctx.beginPath();
      ctx.arc(f * 13, -5.5, 5.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#8a8f96';
      ctx.beginPath();
      ctx.arc(f * 13, -5.5, 2.1, 0, Math.PI * 2);
      ctx.fill();
      // Chassis + hood toward the facing side.
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.roundRect(-13, -27, 26, 13, 3);
      ctx.fill();
      ctx.fillStyle = P.shade;
      ctx.beginPath();
      ctx.roundRect(f * 2, -22, f * 12, 8, 2);
      ctx.fill();
      // Seat back behind the driver.
      ctx.fillStyle = P.accent;
      ctx.beginPath();
      ctx.roundRect(-f * 12 - 3, -34, 7, 10, 2);
      ctx.fill();
      // Exhaust stack.
      ctx.fillStyle = '#2f3336';
      ctx.beginPath();
      ctx.roundRect(-f * 2 - 2, -38, 4, 14, 1.5);
      ctx.fill();
      // Puff of smoke now and then (purely cosmetic).
      ctx.fillStyle = 'rgba(180,186,196,0.5)';
      const puff = (t * 0.7 + e.id) % 1;
      ctx.beginPath();
      ctx.arc(-f * 2, -40 - puff * 10, 2.5 + puff * 3, 0, Math.PI * 2);
      ctx.fill();
      // Headlights ARE the eyes.
      drawEyes(ctx, spec, f * 8, -25, 4.5, 3.6, lookX, lookY, false);
      drawMouth(ctx, spec, f * 6, -21);
      break;
    }
    case 'object': {
      const potted = spec.quirks.includes('petals');
      if (potted) {
        // Flower pot: tapered terracotta with a rim, soil, and a bloom.
        ctx.fillStyle = P.body;
        ctx.beginPath();
        ctx.moveTo(-11, 0);
        ctx.lineTo(11, 0);
        ctx.lineTo(8.5, -17);
        ctx.lineTo(-8.5, -17);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = P.shade;
        ctx.beginPath();
        ctx.roundRect(-12, -22, 24, 6.5, 2);
        ctx.fill();
        ctx.fillStyle = '#4a3423';
        ctx.beginPath();
        ctx.ellipse(0, -21, 8.5, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        // Stem + swaying bloom.
        const sway = Math.sin(t * 1.6 + e.id) * 2;
        ctx.strokeStyle = '#3f7d38';
        ctx.lineWidth = 2.6;
        ctx.beginPath();
        ctx.moveTo(0, -22);
        ctx.quadraticCurveTo(sway * 0.4, -30, sway, -36);
        ctx.stroke();
        ctx.fillStyle = P.accent;
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + t * 0.4;
          ctx.beginPath();
          ctx.ellipse(sway + Math.cos(a) * 5.5, -36 + Math.sin(a) * 5.5, 3.6, 2.6, a, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = '#ffd76a';
        ctx.beginPath();
        ctx.arc(sway, -36, 3, 0, Math.PI * 2);
        ctx.fill();
        drawEyes(ctx, spec, 0, -10, 5, 3.4, lookX, lookY, false);
        drawMouth(ctx, spec, 0, -5);
      } else {
        // Rock: lumpy polygon with facet shading.
        ctx.fillStyle = P.body;
        ctx.beginPath();
        ctx.moveTo(-14, 0);
        ctx.lineTo(-15.5, -7);
        ctx.lineTo(-9, -17);
        ctx.lineTo(2, -20.5);
        ctx.lineTo(12, -14);
        ctx.lineTo(15, -5);
        ctx.lineTo(13, 0);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = P.shade;
        ctx.beginPath();
        ctx.moveTo(4, -19);
        ctx.lineTo(12, -14);
        ctx.lineTo(15, -5);
        ctx.lineTo(13, 0);
        ctx.lineTo(6, 0);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(-9, -17);
        ctx.lineTo(-3, -8);
        ctx.lineTo(2, -13);
        ctx.stroke();
        drawEyes(ctx, spec, 0, -11, 5.5, 3.6, lookX, lookY, false);
        drawMouth(ctx, spec, 0, -5);
      }
      break;
    }
    case 'tree': {
      // Trunk with legs-ish roots and a leafy canopy over the face.
      ctx.fillStyle = P.shade;
      ctx.beginPath(); // root flare
      ctx.moveTo(-9, 0);
      ctx.quadraticCurveTo(-6, -8, -5, -14);
      ctx.lineTo(5, -14);
      ctx.quadraticCurveTo(6, -8, 9, 0);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = P.body;
      ctx.beginPath();
      ctx.roundRect(-5, -28, 10, 18, 3);
      ctx.fill();
      // Canopy: three overlapping blobs.
      ctx.fillStyle = P.accent;
      ctx.beginPath();
      ctx.arc(-9, -33, 9, 0, Math.PI * 2);
      ctx.arc(9, -33, 9, 0, Math.PI * 2);
      ctx.arc(0, -40, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      ctx.beginPath();
      ctx.arc(5, -30, 7, 0, Math.PI * 2);
      ctx.fill();
      if (spec.quirks.includes('leafy')) {
        // Loose leaves that flutter off in the breeze.
        ctx.fillStyle = P.accent;
        for (let i = 0; i < 3; i++) {
          const lf = (t * 0.5 + i * 0.33) % 1;
          const lx = -14 + i * 12 + Math.sin(lf * 6.28 + i) * 5;
          const ly = -44 - lf * 8;
          ctx.globalAlpha = 0.85 - lf * 0.6;
          ctx.beginPath();
          ctx.ellipse(lx, ly, 3, 1.7, lf * 3 + i, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
      drawEyes(ctx, spec, 0, -16, 4.6, 3.6, lookX, lookY, false);
      drawMouth(ctx, spec, 0, -10);
      break;
    }
  }

  // Species quirk sprinkles that sit on any plan.
  if (spec.quirks.includes('petals') && spec.body !== 'stalk') {
    ctx.fillStyle = P.accent;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * 15, -26 + Math.sin(a) * 4, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Held gear: launchers ride in both hands, aimed where the eyes look —
  // the character IS the piece of artillery, so the tube never leaves the grip.
  // Barrelless fruit takes the same branch: the weapon itself rides the hand
  // and swings with the aim, so EVERY held weapon is drawn — the four bespoke
  // tubes below, and anything else as its own flight art at the grip.
  if (o.held && !dead) {
    const cannon = o.held === 'coconut';
    const mortar = o.held === 'carrot';
    const bamboo = o.held === 'bamboo';
    const ang = Math.atan2(lookYRaw, lookXRaw);
    ctx.save();
    ctx.translate(e.facing * 3, -15);
    ctx.rotate(ang);
    if (cannon) {
      // Chunky bronze field cannon for the coconut shell.
      ctx.fillStyle = '#4a3a22'; // breech
      ctx.beginPath();
      ctx.roundRect(-10, -5, 12, 10, 3);
      ctx.fill();
      ctx.fillStyle = '#8a6a3a'; // barrel
      ctx.beginPath();
      ctx.roundRect(0, -6, 27, 12, 4);
      ctx.fill();
      ctx.fillStyle = '#a8834a'; // muzzle band
      ctx.beginPath();
      ctx.roundRect(22, -7.4, 7, 14.8, 3);
      ctx.fill();
      ctx.fillStyle = '#241a10'; // bore
      ctx.beginPath();
      ctx.arc(29, 0, 3.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#6b5230'; // reinforcing ring
      ctx.beginPath();
      ctx.roundRect(10, -6.6, 3.4, 13.2, 1);
      ctx.fill();
    } else if (mortar) {
      // Lashed wooden pipe — three carrots live in here.
      ctx.fillStyle = '#6b4a2f'; // raw pipe
      ctx.beginPath();
      ctx.roundRect(-4, -6.5, 30, 13, 5);
      ctx.fill();
      ctx.fillStyle = '#8a6a42'; // wood face
      ctx.beginPath();
      ctx.roundRect(-2, -5, 26, 10, 4);
      ctx.fill();
      ctx.strokeStyle = '#4a3423'; // grain + lashings
      ctx.lineWidth = 1.6;
      for (const bx of [4, 14]) {
        ctx.beginPath();
        ctx.moveTo(bx, -6.5);
        ctx.lineTo(bx, 6.5);
        ctx.stroke();
      }
      ctx.strokeStyle = 'rgba(74,52,35,.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, -2);
      ctx.lineTo(24, -1);
      ctx.moveTo(0, 2);
      ctx.lineTo(24, 3);
      ctx.stroke();
      ctx.fillStyle = '#2c2013'; // mouth of the pipe
      ctx.beginPath();
      ctx.ellipse(26, 0, 3, 6, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (bamboo) {
      // Polished bamboo blowgun pipe: lit crown, shaded belly, node rings at
      // every joint, a lashed mouthpiece, and a pale cut-face muzzle.
      const body = ctx.createLinearGradient(0, -4.6, 0, 4.6);
      body.addColorStop(0, '#a8d98f');
      body.addColorStop(0.5, '#7cb86a');
      body.addColorStop(1, '#4e8b42');
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.roundRect(-11, -4.6, 40, 9.2, 4.6);
      ctx.fill();
      // Faint grain between the nodes.
      ctx.strokeStyle = 'rgba(63,122,49,.18)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-4, -1.5);
      ctx.lineTo(25, -2.5);
      ctx.moveTo(-4, 2);
      ctx.lineTo(25, 3);
      ctx.stroke();
      // Node rings: slightly proud bands at each segment joint.
      for (const nx of [1, 14, 26]) {
        ctx.fillStyle = '#3f7a31';
        ctx.beginPath();
        ctx.roundRect(nx - 1.7, -5.3, 3.4, 10.6, 1.4);
        ctx.fill();
        ctx.strokeStyle = 'rgba(30,60,24,.7)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(nx - 1.7, -0.8);
        ctx.lineTo(nx + 1.7, -0.8);
        ctx.stroke();
      }
      // Rear mouthpiece: dark cap with crossed cord lashings.
      ctx.fillStyle = '#2f4a26';
      ctx.beginPath();
      ctx.roundRect(-11, -4.6, 5.4, 9.2, 3);
      ctx.fill();
      ctx.strokeStyle = '#c9a86a';
      ctx.lineWidth = 1.4;
      for (const cx of [-9, -6.6]) {
        ctx.beginPath();
        ctx.moveTo(cx - 1.4, -4);
        ctx.lineTo(cx + 1.4, -1);
        ctx.moveTo(cx - 1.4, 1);
        ctx.lineTo(cx + 1.4, 4);
        ctx.stroke();
      }
      // Specular highlights along the lit top of each segment.
      ctx.strokeStyle = 'rgba(255,255,255,.35)';
      ctx.lineWidth = 1.4;
      ctx.lineCap = 'round';
      for (const [sx, sw] of [
        [-4, 4],
        [3.5, 9],
        [16.5, 8],
      ] as const) {
        ctx.beginPath();
        ctx.moveTo(sx, -3.2);
        ctx.lineTo(sx + sw, -3.2);
        ctx.stroke();
      }
      // Muzzle: pale cut face with pith, dark bore, inner shadow, rim nick.
      ctx.fillStyle = '#cfe8b8';
      ctx.beginPath();
      ctx.ellipse(28.6, 0, 2.6, 5.4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#16240f';
      ctx.beginPath();
      ctx.arc(29.2, 0, 2.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,.35)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(29.2, 0, 2.8, Math.PI * 0.5, Math.PI * 1.5);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,.55)';
      ctx.beginPath();
      ctx.ellipse(27.6, -3.4, 1, 1.4, -0.4, 0, Math.PI * 2);
      ctx.fill();
    } else if (o.held === 'beans') {
      // Green bean pod launcher — the same plump pod the burst fires, grown
      // to launcher size: beans swelling inside, seam along the spine, stem
      // nub at the tail, and a little bean-mouth at the tip where the volley
      // spits out. Same palette as the in-flight pod so they read as one thing.
      const pod = () => {
        ctx.beginPath();
        ctx.moveTo(26, 0);
        ctx.quadraticCurveTo(18, -8.8, 3, -8.6);
        ctx.quadraticCurveTo(-7.5, -8, -10.5, -1.4);
        ctx.quadraticCurveTo(-7.5, 7.4, 3, 8.6);
        ctx.quadraticCurveTo(18, 8.4, 26, 0);
        ctx.closePath();
      };
      ctx.fillStyle = '#8fcf6a';
      pod();
      ctx.fill();
      // Three beans bulge the pod.
      ctx.fillStyle = '#a8dd84';
      for (const bx of [16, 5, -6]) {
        ctx.beginPath();
        ctx.ellipse(bx, -0.6, 4.6, 5.4, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // Seam along the spine.
      ctx.strokeStyle = 'rgba(52,104,44,.75)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(22, 3);
      ctx.quadraticCurveTo(4, 7.6, -9.5, 1.6);
      ctx.stroke();
      // Outline, then the stem nub at the tail.
      ctx.strokeStyle = 'rgba(38,74,32,.85)';
      ctx.lineWidth = 1.7;
      pod();
      ctx.stroke();
      ctx.fillStyle = '#5e8f3e';
      ctx.beginPath();
      ctx.ellipse(-11.4, -1.6, 2.6, 1.8, 0.4, 0, Math.PI * 2);
      ctx.fill();
      // Muzzle: the pod's bean-mouth, where the burst comes out.
      ctx.fillStyle = 'rgba(28,58,22,.9)';
      ctx.beginPath();
      ctx.ellipse(24.2, 0, 1.9, 3.1, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Barrelless weapons — chili, pomegranate, hen, grapes, watermelon,
      // pigeon, beehive, lemon, and anything added later — held out along the
      // aim line by replaying their own flight art at the grip: what the hand
      // holds is pixel-identical to what fires, automatically. This frame is
      // already at the hand and rotated to the aim, so the fake projectile
      // sits at the origin pointing down +x. Sway/flap/flicker keep breathing
      // with the wall clock; age-tumbles freeze and the particle sink is a
      // no-op (a held chili keeps its flame but never sparks at the holder).
      ctx.translate(8, 0); // held out front — the launchers' reach
      drawHeldWeaponArt(ctx, o.held, t);
    }
    ctx.restore();
  }

  // Hurt overlay: hot red flash proportional to poseTimer.
  if (hurt) {
    ctx.globalAlpha = 0.4 * clamp(e.poseTimer / 0.4, 0, 1);
    ctx.fillStyle = '#ff4a3d';
    ctx.beginPath();
    ctx.ellipse(0, -16, 16, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // Dead face: × eyes drawn over whatever pose produced.
  if (dead) {
    ctx.strokeStyle = '#20242c';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (const side of [-1, 1]) {
      const ex = side * 4;
      const ey =
        spec.body === 'stalk'
          ? -36
          : spec.body === 'winged'
            ? -21
            : spec.body === 'beast' || spec.body === 'machine'
              ? -25
              : spec.body === 'object'
                ? -11
                : -16;
      ctx.moveTo(ex - 3, ey - 3);
      ctx.lineTo(ex + 3, ey + 3);
      ctx.moveTo(ex + 3, ey - 3);
      ctx.lineTo(ex - 3, ey + 3);
    }
    ctx.stroke();
  }

  ctx.restore();
}
